"use client";

import Link from "next/link";
import Image from "next/image";
import { useState, useTransition } from "react";
import { Plus, Minus, Trash2, ShoppingBag, Tag, X } from "lucide-react";
import { useCart, formatDkk } from "@/lib/cart/cart-context";

type Props = { locale: "da" | "en" };

type CouponState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "valid"; code: string; discount_dkk: number; description: string | null }
  | { status: "invalid"; message: string };

export function CartView({ locale }: Props) {
  const { items, subtotal, isHydrated, updateQuantity, removeItem, clear, couponCode, setCouponCode } = useCart();
  const [couponInput, setCouponInput] = useState(couponCode ?? "");
  const [coupon, setCoupon] = useState<CouponState>({ status: "idle" });
  const [, startTransition] = useTransition();

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCoupon({ status: "loading" });
    try {
      const res = await fetch("/api/cart/apply-coupon", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: couponInput.trim(), subtotal_dkk: subtotal }),
      });
      const data = await res.json();
      if (data.valid) {
        setCoupon({
          status: "valid",
          code: data.code,
          discount_dkk: data.discount_dkk,
          description: data.description,
        });
        startTransition(() => setCouponCode(data.code));
      } else {
        setCoupon({ status: "invalid", message: data.message ?? "Ugyldig kode" });
      }
    } catch {
      setCoupon({ status: "invalid", message: "Kunne ikke validere kode" });
    }
  };

  const removeCoupon = () => {
    setCouponInput("");
    setCoupon({ status: "idle" });
    startTransition(() => setCouponCode(null));
  };

  const discount = coupon.status === "valid" ? coupon.discount_dkk : 0;
  const total = Math.max(0, subtotal - discount);

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-20 bg-[var(--color-surface-50)]">
        <div className="container-wide max-w-[920px]">
          <div className="bg-white border border-[var(--color-line)] rounded-[14px] p-12 text-center text-[var(--color-muted)]">
            {locale === "en" ? "Loading cart…" : "Indlæser kurv…"}
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section className="py-16 lg:py-20 bg-[var(--color-surface-50)]">
        <div className="container-wide max-w-[640px]">
          <div className="bg-white border border-dashed border-[var(--color-line)] rounded-[14px] p-12 text-center">
            <ShoppingBag className="mx-auto mb-4 text-[var(--color-muted)]" size={40} strokeWidth={1.5} />
            <h2 className="font-[family-name:var(--font-display-loaded)] text-[22px] font-bold text-[var(--color-navy-700)] mb-2">
              {locale === "en" ? "Your cart is empty" : "Din kurv er tom"}
            </h2>
            <p className="text-[var(--color-muted)] text-[14.5px] mb-6">
              {locale === "en" ? "Browse our shop to find materials for your course." : "Se vores butik for materialer til dit forløb."}
            </p>
            <Link href={locale === "en" ? "/en/butik" : "/butik"} className="btn btn-primary !py-2.5">
              {locale === "en" ? "Go to shop" : "Gå til butikken"}
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-10 lg:py-14 bg-[var(--color-surface-50)]">
      <div className="container-wide">
        <div className="grid lg:grid-cols-[1fr_360px] gap-8 max-w-[1100px] mx-auto">
          {/* Items list */}
          <div className="space-y-3">
            {items.map((item) => (
              <article
                key={item.product_id}
                className="bg-white border border-[var(--color-line)] rounded-[12px] p-4 flex items-center gap-4"
              >
                {item.image_url && (
                  <div className="relative w-16 h-16 rounded-[8px] overflow-hidden bg-[var(--color-surface-100)] flex-shrink-0">
                    <Image src={item.image_url} alt={item.image_alt ?? item.name} fill className="object-cover" sizes="64px" unoptimized />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <Link
                    href={locale === "en" ? `/en/butik#${item.slug}` : `/butik#${item.slug}`}
                    className="font-semibold text-[14.5px] text-[var(--color-navy-700)] hover:underline line-clamp-2"
                  >
                    {item.name}
                  </Link>
                  <div className="text-[13px] text-[var(--color-muted)] tabular-nums mt-0.5">
                    {formatDkk(item.price_dkk, locale)} {locale === "en" ? "each" : "stk"}
                  </div>
                </div>

                <div className="inline-flex items-center bg-[var(--color-surface-50)] border border-[var(--color-line)] rounded-[8px] flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                    className="px-2 py-2 text-[var(--color-muted)] hover:text-[var(--color-navy-700)]"
                    aria-label={locale === "en" ? "Decrease" : "Færre"}
                  >
                    <Minus size={13} strokeWidth={2.5} />
                  </button>
                  <span className="px-2 font-bold text-[13.5px] tabular-nums min-w-[24px] text-center">{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                    className="px-2 py-2 text-[var(--color-muted)] hover:text-[var(--color-navy-700)]"
                    aria-label={locale === "en" ? "Increase" : "Flere"}
                  >
                    <Plus size={13} strokeWidth={2.5} />
                  </button>
                </div>

                <div className="font-[family-name:var(--font-display-loaded)] text-[16px] font-bold text-[var(--color-navy-700)] tabular-nums whitespace-nowrap min-w-[90px] text-right">
                  {formatDkk(item.price_dkk * item.quantity, locale)}
                </div>

                <button
                  type="button"
                  onClick={() => removeItem(item.product_id)}
                  className="p-2 rounded-[6px] text-[#991b1b] hover:bg-[#fef2f2] flex-shrink-0"
                  aria-label={locale === "en" ? "Remove" : "Fjern"}
                >
                  <Trash2 size={14} strokeWidth={2} />
                </button>
              </article>
            ))}

            <div className="flex items-center justify-between pt-2">
              <Link
                href={locale === "en" ? "/en/butik" : "/butik"}
                className="text-[13px] text-[var(--color-primary-700)] hover:underline font-semibold"
              >
                ← {locale === "en" ? "Continue shopping" : "Tilbage til butikken"}
              </Link>
              <button
                type="button"
                onClick={() => {
                  if (confirm(locale === "en" ? "Empty cart?" : "Tøm kurv?")) clear();
                }}
                className="text-[12.5px] text-[var(--color-muted)] hover:text-[#991b1b] hover:underline"
              >
                {locale === "en" ? "Empty cart" : "Tøm kurv"}
              </button>
            </div>
          </div>

          {/* Summary */}
          <aside className="lg:sticky lg:top-6 self-start">
            <div className="bg-white border border-[var(--color-line)] rounded-[14px] p-6">
              <h2 className="font-[family-name:var(--font-display-loaded)] text-[18px] font-bold text-[var(--color-navy-700)] mb-4">
                {locale === "en" ? "Order summary" : "Ordreoversigt"}
              </h2>

              {/* Coupon */}
              <div className="mb-5">
                <label className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-muted)] font-bold mb-2 block">
                  <Tag size={11} className="inline mr-1" />
                  {locale === "en" ? "Discount code" : "Rabatkode"}
                </label>
                {coupon.status === "valid" ? (
                  <div className="bg-[#dcfce7] border border-[#86efac] rounded-[8px] p-3 flex items-center gap-2">
                    <Tag size={14} className="text-[#166534]" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-[#166534]">{coupon.code}</div>
                      {coupon.description && (
                        <div className="text-[11.5px] text-[#166534]/80">{coupon.description}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={removeCoupon}
                      className="p-1 text-[#166534] hover:bg-[#bbf7d0] rounded"
                      aria-label={locale === "en" ? "Remove" : "Fjern"}
                    >
                      <X size={14} strokeWidth={2.5} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder={locale === "en" ? "Enter code" : "Indtast kode"}
                      className="input flex-1 !py-2 text-[13px] uppercase font-mono"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyCoupon();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={applyCoupon}
                      disabled={!couponInput.trim() || coupon.status === "loading"}
                      className="btn btn-ghost !py-2 !px-4 text-[13px]"
                    >
                      {coupon.status === "loading" ? "…" : locale === "en" ? "Apply" : "Anvend"}
                    </button>
                  </div>
                )}
                {coupon.status === "invalid" && (
                  <p className="text-[12px] text-[#991b1b] mt-1.5">{coupon.message}</p>
                )}
              </div>

              {/* Totals */}
              <div className="space-y-2 text-[14px] mb-5 pt-4 border-t border-[var(--color-line)]">
                <div className="flex justify-between text-[var(--color-muted)]">
                  <span>{locale === "en" ? "Subtotal" : "Subtotal"}</span>
                  <span className="tabular-nums">{formatDkk(subtotal, locale)}</span>
                </div>
                {coupon.status === "valid" && (
                  <div className="flex justify-between text-[#166534]">
                    <span>{locale === "en" ? "Discount" : "Rabat"} ({coupon.code})</span>
                    <span className="tabular-nums">−{formatDkk(coupon.discount_dkk, locale)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t border-[var(--color-line)] mt-3">
                  <span className="font-[family-name:var(--font-display-loaded)] text-[18px] font-bold text-[var(--color-navy-700)]">
                    {locale === "en" ? "Total" : "Total"}
                  </span>
                  <span className="font-[family-name:var(--font-display-loaded)] text-[20px] font-bold text-[var(--color-navy-700)] tabular-nums">
                    {formatDkk(total, locale)}
                  </span>
                </div>
                <p className="text-[11.5px] text-[var(--color-muted)] text-right">
                  {locale === "en" ? "Incl. 25% VAT" : "Inkl. 25% moms"}
                </p>
              </div>

              <Link
                href={locale === "en" ? "/en/checkout" : "/checkout"}
                className="btn btn-primary w-full !py-3 inline-flex items-center justify-center"
              >
                {locale === "en" ? "Go to checkout" : "Til kassen"} →
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
